/**
 * Extracts diagnostic Sentry tags from an auth-failure-style error so we can
 * tell, after the fact, *which* endpoint denied the request and *why* — i.e.
 * was the token actually invalid (server says "Invalid token.") or did a still
 * valid-token request hit a per-endpoint permission denial that the frontend
 * unfortunately treats as "log out".
 *
 * The shape this inspects is a deliberate convention in this codebase:
 *   - HttpError carries optional `url` and `method` (set by the fetch wrapper).
 *   - Django/DRF responses use `body.detail` for AuthenticationFailed /
 *     NotAuthenticated, `body.permission_error` for soft-denials that should
 *     keep the user logged in, and `body.mfa_status` for MFA gates.
 *
 * All fields are optional; callers can hand this any shape and missing tags
 * are simply omitted.
 */

const TOKEN_INVALID_DETAILS = new Set<string>([
  'Invalid token.',
  'Token has expired.',
  'Not Authenticated.',
])

export type FailureKind =
  | 'token-invalid'
  | 'endpoint-rejected'
  | 'permission-error'
  | 'mfa-required'
  | 'unknown'

interface ApiErrorPayload {
  detail?: string
  permission_error?: any
  mfa_status?: any
}

/**
 * Resolve the parsed API payload from `HttpError.body`, transparently handling
 * the two `body` shapes produced by the codebase's fetch wrappers:
 *
 *   - `spa/app/src/util/fetch.ts` stores the parsed JSON directly as `body`.
 *   - `framework/ra-core/src/dataProvider/fetch.ts` wraps it as
 *     `{ url, body, json }` where `json` is the parsed payload.
 *
 * Returns `undefined` if neither shape carries useful payload fields, so
 * callers can short-circuit cleanly.
 */
const resolveApiPayload = (rawBody: any): ApiErrorPayload | undefined => {
  if (!rawBody || typeof rawBody !== 'object') return undefined
  // ra-core wrapper: prefer the unwrapped `json` if it looks like the payload.
  if (rawBody.json && typeof rawBody.json === 'object') return rawBody.json as ApiErrorPayload
  return rawBody as ApiErrorPayload
}

/**
 * Classify an auth-failure error into a coarse-grained kind for Sentry filtering.
 *
 * - `mfa-required` and `permission-error` should not result in a logout; tag
 *   them so we can confirm the gates upstream are working.
 * - `token-invalid` is a real session-end signal.
 * - `endpoint-rejected` is the suspicious bucket — a 401/403 from a
 *   still-valid token. These are the cases that prematurely log users out.
 */
export const classifyAuthFailure = (error: any): FailureKind => {
  if (!error || typeof error !== 'object') return 'unknown'
  const status = error.status as number | undefined
  const body = resolveApiPayload(error.body)

  if (body?.mfa_status) return 'mfa-required'
  if (body?.permission_error) return 'permission-error'
  if (status !== 401 && status !== 403) return 'unknown'

  const detail = typeof body?.detail === 'string' ? body.detail : undefined
  if (status === 401 && detail) {
    if (TOKEN_INVALID_DETAILS.has(detail)) return 'token-invalid'
    if (detail.startsWith('Invalid token header.')) return 'token-invalid'
  }

  return 'endpoint-rejected'
}

/**
 * Strip query string and replace numeric path segments with `{id}` so that
 * cardinality stays low in Sentry tag values (which are limited to ~200 chars
 * and indexed by exact value).
 */
const normalizeEndpointUrl = (raw: string | undefined): string | undefined => {
  if (!raw) return undefined
  let path: string
  try {
    const u = new URL(raw, 'https://placeholder.invalid')
    path = u.pathname
  } catch {
    path = raw.split('?')[0]
  }
  return path.replace(/\/\d+(?=\/|$)/g, '/{id}')
}

const truncate = (s: string, max: number): string => (s.length > max ? s.slice(0, max) : s)

/**
 * Build the Sentry `tags` object for an auth-failure capture. Caller spreads
 * this into the `captureContext.tags` it already builds, e.g.:
 *
 *   captureContext: {
 *     tags: { reason: params.reason, ...buildAuthFailureTags(params.cause) },
 *   }
 */
export const buildAuthFailureTags = (error: any): Record<string, string | boolean> => {
  const tags: Record<string, string | boolean> = {}
  if (!error || typeof error !== 'object') return tags

  const url = normalizeEndpointUrl(typeof error.url === 'string' ? error.url : undefined)
  if (url) tags.endpoint_url = url

  if (typeof error.method === 'string') tags.endpoint_method = error.method

  if (typeof error.status === 'number') tags.response_status = String(error.status)

  const payload = resolveApiPayload(error.body)
  const detail = payload && typeof payload.detail === 'string' ? payload.detail : undefined
  if (detail) tags.response_detail = truncate(detail, 80)

  if (payload) {
    tags.has_permission_error = !!payload.permission_error
    tags.has_mfa_status = !!payload.mfa_status
  }

  tags.failure_kind = classifyAuthFailure(error)

  return tags
}
